import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { doDelete, get, patch, post } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';
import GalleryItemStructureEditor from '../../../helpers/GalleryItemStructureEditor';

function TemplateForm({ websiteId, initial, onSaved, onCancel }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name ?? '');
  const [columns, setColumns] = useState(initial ? { columns: initial.columns ?? [] } : { columns: [] });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required.');
      return;
    }
    try {
      setSaving(true);
      const payload = { name: name.trim(), columns: columns.columns ?? [] };
      let result;
      if (isEdit) {
        result = await patch(`websites/${websiteId}/gallery-templates/${initial.id}`, payload);
      } else {
        result = await post(`websites/${websiteId}/gallery-templates`, payload);
      }
      onSaved(result.gallery_template);
    } catch {
      /* httpClient shows toast */
    } finally {
      setSaving(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="border rounded p-3 mb-3">
      <Form.Group className="mb-3">
        <Form.Label>Name</Form.Label>
        <Form.Control
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Template name"
        />
      </Form.Group>

      <GalleryItemStructureEditor value={columns} onChange={setColumns} />

      <div className="form-actions mt-2">
        <Button type="submit" className="primary-button" disabled={saving}>
          {saving ? <><Spinner animation="border" size="sm" className="me-2" />Saving...</> : 'Save'}
        </Button>
        <Button type="button" className="primary-button" onClick={onCancel} disabled={saving}>Cancel</Button>
      </div>
    </Form>
  );
}

function GalleryTemplates({ website }) {
  const websiteId = website.id;

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await get(`websites/${websiteId}/gallery-templates`);
      setTemplates(resp?.gallery_templates ?? []);
    } catch {
      /* httpClient shows toast */
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => { load(); }, [load]);

  const handleCreated = (template) => {
    setTemplates((prev) => [...prev, template]);
    setCreating(false);
  };

  const handleUpdated = (template) => {
    setTemplates((prev) => prev.map((t) => (t.id === template.id ? template : t)));
    setEditingId(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this gallery template?')) return;
    try {
      setDeletingId(id);
      await doDelete(`websites/${websiteId}/gallery-templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch {
      /* httpClient shows toast */
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="text-center my-4"><Spinner animation="border" /></div>;
  }

  return (
    <div>
      <div className="structure-node-header mb-3">
        <strong>Gallery templates</strong>
        {!creating && (
          <Button type="button" className="primary-button" onClick={() => setCreating(true)}>New template</Button>
        )}
      </div>

      {creating && (
        <TemplateForm
          websiteId={websiteId}
          onSaved={handleCreated}
          onCancel={() => setCreating(false)}
        />
      )}

      {templates.length === 0 && !creating && (
        <Alert variant="secondary">No gallery templates yet.</Alert>
      )}

      {templates.map((t) => (
        <div key={t.id} className="border rounded p-3 mb-2">
          {editingId === t.id ? (
            <TemplateForm
              websiteId={websiteId}
              initial={t}
              onSaved={handleUpdated}
              onCancel={() => setEditingId(null)}
            />
          ) : (
            <div className="structure-node-header">
              <span>
                <strong>{t.name}</strong>
                <span className="text-secondary ms-2">
                  {(t.columns ?? []).length} column{(t.columns ?? []).length !== 1 ? 's' : ''},&nbsp;
                  {(t.columns ?? []).reduce((s, c) => s + (c.properties ?? []).length, 0)} properties
                </span>
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button type="button" className="primary-button" onClick={() => setEditingId(t.id)}>Edit</Button>
                <Button
                  type="button"
                  className="primary-button"
                  onClick={() => handleDelete(t.id)}
                  disabled={deletingId === t.id}
                >
                  {deletingId === t.id ? <Spinner animation="border" size="sm" /> : 'Delete'}
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default GalleryTemplates;
